import {
  Chart,
  formatNumber,
  DropdownSearch,
  OverflowText,
  getColorFromCssVariable,
} from "ui";
import { ChartCard } from "./ChartCard";
import { useErrorCodeFilter } from "./useErrorCodeFilter";
import { useMemo, useEffect } from "react";
import { useDatadogQuery } from "./useDatadogQuery";
import { formatChartLabel } from "./utils";
import { useDeclineTypeFilter } from "./useDeclineTypeFilter";

const ERROR_CODE_COLOR_VARS = [
  "--chart-categorical-6-subtler",
  "--chart-categorical-7-subtler",
  "--chart-categorical-5-subtler",
  "--chart-categorical-4-subtler",
];
const OTHERS_COLOR_VAR = "--chart-categorical-8-subtler";

type DailyDeclinesData = Record<
  string,
  { label: string; value: number; type: "SOFT_DECLINE" | "HARD_DECLINE" }[]
>;

export function DailyDeclinesChart({
  isSameDayRange,
}: {
  isSameDayRange: boolean;
}) {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<DailyDeclinesData>({
      chartType: "DAILY_ERROR_CODES_COUNTS",
      delayMs: 2100,
    });

  const { selectedType, setSelectedType, typeOptions } = useDeclineTypeFilter();
  const selectedDeclineType = selectedType[0];

  const { labels, errorCodeData, allDeclineReasons } = useMemo(() => {
    if (data && Object.keys(data).length > 0) {
      const labels = Object.keys(data);

      const filteredByType: DailyDeclinesData = {};
      labels.forEach((time) => {
        const entries = data[time];
        filteredByType[time] =
          selectedDeclineType === "ALL_TYPES"
            ? entries
            : entries.filter((entry) => entry.type === selectedDeclineType);
      });

      // Build errorCodeData: { error_code: [count_per_day] } and totals
      const errorCodeData: Record<string, number[]> = {};
      const errorCodeTotals: Record<string, number> = {};

      labels.forEach((time, dayIndex) => {
        filteredByType[time].forEach(({ label, value }) => {
          if (!errorCodeData[label]) {
            errorCodeData[label] = new Array(labels.length).fill(0);
            errorCodeTotals[label] = 0;
          }
          errorCodeData[label][dayIndex] = value;
          errorCodeTotals[label] += value;
        });
      });

      // Create allDeclineReasons sorted by value (highest to lowest)
      const allDeclineReasons = Object.entries(errorCodeTotals)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);

      return {
        labels,
        errorCodeData,
        allDeclineReasons,
      };
    }

    return {
      labels: [] as string[],
      errorCodeData: {} as Record<string, number[]>,
      allDeclineReasons: [] as { label: string; value: number }[],
    };
  }, [data, selectedDeclineType]);

  const {
    selectedErrorCodes,
    setSelectedErrorCodes,
    errorCodeOptions,
    filteredData,
    hasOthers,
    isItemDisabled,
  } = useErrorCodeFilter(allDeclineReasons);

  // Reset error codes when decline type changes
  useEffect(() => {
    setSelectedErrorCodes([]);
  }, [selectedDeclineType, setSelectedErrorCodes]);

  // Build datasets from filtered data
  const datasets = useMemo(() => {
    const baseDatasets = filteredData
      .filter((item) => item.label !== "OTHERS")
      .map((item, index) => {
        const colorVar =
          ERROR_CODE_COLOR_VARS[index % ERROR_CODE_COLOR_VARS.length];
        const color = getColorFromCssVariable(colorVar);
        return {
          label: item.label,
          data: errorCodeData[item.label] || [],
          backgroundColor: color,
          borderColor: color,
          stack: "stack0",
        };
      });

    // Add OTHERS dataset if needed
    if (hasOthers) {
      const selectedLabels = filteredData
        .filter((item) => item.label !== "OTHERS")
        .map((item) => item.label);
      const unselectedCodes = Object.keys(errorCodeData).filter(
        (code) => !selectedLabels.includes(code)
      );

      const othersData = labels.map((_, dayIndex) => {
        return unselectedCodes.reduce((sum, code) => {
          const codeData = errorCodeData[code];
          return sum + (codeData?.[dayIndex] || 0);
        }, 0);
      });

      const othersColor = getColorFromCssVariable(OTHERS_COLOR_VAR);
      baseDatasets.push({
        label: "OTHERS",
        data: othersData,
        backgroundColor: othersColor,
        borderColor: othersColor,
        stack: "stack0",
      });
    }

    return baseDatasets;
  }, [filteredData, errorCodeData, hasOthers, labels]);

  return (
    <ChartCard
      ref={ref}
      title="Daily declines"
      description="Number of declined payments per day."
      contentClassName="pr-0 pl-4"
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
      headerAction={
        <div className="flex gap-2">
          <DropdownSearch
            label={selectedType[1]}
            options={typeOptions}
            selected={selectedType}
            onSelect={setSelectedType}
            className="w-auto"
            contentProps={{ align: "start" }}
          />
          <DropdownSearch
            label="All error codes"
            placeholder="Search error codes"
            options={errorCodeOptions}
            selected={selectedErrorCodes}
            onSelect={setSelectedErrorCodes}
            multiple
            withConfirmation
            className="w-40"
            isItemDisabled={isItemDisabled}
            contentProps={{ align: "start" }}
            disableSelectAll
            displayedValue={(value) => (
              <span className="text-foundations-bolder">
                {value.length > 0
                  ? `${value.length} error${
                      value.length > 1 ? "s" : ""
                    } selected`
                  : "All error codes"}
              </span>
            )}
            renderOption={(label) => <OverflowText>{label}</OverflowText>}
          />
        </div>
      }
    >
      <Chart type="bar" labels={labels} datasets={datasets} className="h-full">
        <Chart.Legend variant="row" />
        <Chart.Canvas
          className="h-0 flex-1"
          formatYLabel={(v) => formatNumber({ value: v, compact: true })}
          formatXLabel={(value) => formatChartLabel({ value, isSameDayRange })}
        />
        <Chart.Tooltip
          valueFormatter={(value) => formatNumber({ value })}
          labelFormatter={(value) =>
            formatChartLabel({ value, isSameDayRange })
          }
        />
      </Chart>
    </ChartCard>
  );
}
